import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { exportUsers } from '../redux/UsersSlice';
import { fetchInterests, fetchSkills } from '../redux/AttributesSlice';

const ExportUsersModal = ({ isOpen, onClose, currentFilter = 'all' }) => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.users);
    const { skills, interests } = useSelector((state) => state.attributes);
    console.log(skills)

    const [filters, setFilters] = useState({
        // Existing filters
        first_name: '',
        last_name: '',
        email: '',
        location: '',
        status: currentFilter !== 'all' ? currentFilter : '',
        response_deployment: '',
        mentee: '',
        mentor: '',

        // Added filters
        middle_name: '',
        sex: '',
        created_at: '',
        updated_at: '',
        fetp_level: '',
        skills: '',
        interests: ''
    });

    useEffect(() => {
        dispatch(fetchSkills());
        dispatch(fetchInterests());
    }, [dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Prepare filter parameters
        const filterParams = {};

        // Add non-empty filters and convert boolean strings to 1/0
        Object.entries(filters).forEach(([key, value]) => {
            if (value) {
                // Convert "true"/"false" to 1/0 for boolean fields
                if (key === 'response_deployment' || key === 'mentee' || key === 'mentor') {
                    filterParams[key] = value === 'true' ? 1 : 0;
                } else {
                    filterParams[key] = value;
                }
            }
        });

        // Dispatch export action
        dispatch(exportUsers({
            filter: Object.values(filterParams).join(','),
            filter_type: Object.keys(filterParams).join(',')
        }))
            .unwrap()
            .then(() => {
                toast.success('Export request submitted! You will receive an email when ready.');
                onClose();
            })
            .catch((error) => {
                toast.error(error.message || 'Failed to export users');
            });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center border-b px-6 py-4">
                    <h2 className="text-xl font-semibold">Export Users</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    <p className="mb-4 text-gray-600">
                        Define filters for exporting users. The export will be sent to your email when ready.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {/* Personal Information Filters */}
                        <div className="md:col-span-2">
                            <h3 className="text-md font-medium text-gray-900 mb-3">Personal Information</h3>
                        </div>

                        {/* First Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                First Name
                            </label>
                            <input
                                type="text"
                                name="first_name"
                                value={filters.first_name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Filter by first name"
                            />
                        </div>

                        {/* Middle Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Middle Name
                            </label>
                            <input
                                type="text"
                                name="middle_name"
                                value={filters.middle_name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Filter by middle name"
                            />
                        </div>

                        {/* Last Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Last Name
                            </label>
                            <input
                                type="text"
                                name="last_name"
                                value={filters.last_name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Filter by last name"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                type="text"
                                name="email"
                                value={filters.email}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Filter by email"
                            />
                        </div>

                        {/* Sex/Gender */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Gender
                            </label>
                            <select
                                name="sex"
                                value={filters.sex}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All genders</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>

                        {/* Location */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Location
                            </label>
                            <input
                                type="text"
                                name="location"
                                value={filters.location}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Filter by location"
                            />
                        </div>

                        {/* Account Information */}
                        <div className="md:col-span-2 mt-4">
                            <h3 className="text-md font-medium text-gray-900 mb-3">Account Information</h3>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                name="status"
                                value={filters.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All statuses</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        {/* Created At */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Created Date
                            </label>
                            <input
                                type="date"
                                name="created_at"
                                value={filters.created_at}
                                onChange={handleDateChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Updated At */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Last Updated Date
                            </label>
                            <input
                                type="date"
                                name="updated_at"
                                value={filters.updated_at}
                                onChange={handleDateChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Professional Information */}
                        <div className="md:col-span-2 mt-4">
                            <h3 className="text-md font-medium text-gray-900 mb-3">Professional Information</h3>
                        </div>

                        {/* FETP Level */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                FETP Level
                            </label>
                            <select
                                name="fetp_level"
                                value={filters.fetp_level}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All levels</option>
                                <option value="frontline">Frontline</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>

                        {/* Skills */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Skills
                            </label>
                            <select
                                name="skills"
                                value={filters.skills}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All skills</option>
                                {skills?.items && skills?.items.length > 0 && skills?.items.map(skill => (
                                    <option key={skill.id} value={skill.name}>
                                        {skill.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Interests */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Interests
                            </label>
                            <select
                                name="interests"
                                value={filters.interests}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All interests</option>
                                {interests?.items && interests?.items.length > 0 && interests?.items.map(interest => (
                                    <option key={interest.id} value={interest.name}>
                                        {interest.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Availability Section */}
                        <div className="md:col-span-2 mt-4">
                            <h3 className="text-md font-medium text-gray-900 mb-3">Availability Information</h3>
                        </div>

                        {/* Response Deployment */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Response Deployment
                            </label>
                            <select
                                name="response_deployment"
                                value={filters.response_deployment}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All</option>
                                <option value="true">Available</option>
                                <option value="false">Not Available</option>
                            </select>
                        </div>

                        {/* Mentee */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Mentee Availability
                            </label>
                            <select
                                name="mentee"
                                value={filters.mentee}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All</option>
                                <option value="true">Available</option>
                                <option value="false">Not Available</option>
                            </select>
                        </div>

                        {/* Mentor */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Mentor Availability
                            </label>
                            <select
                                name="mentor"
                                value={filters.mentor}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All</option>
                                <option value="true">Available</option>
                                <option value="false">Not Available</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${loading ? 'opacity-75 cursor-not-allowed' : ''
                                }`}
                        >
                            {loading ? 'Submitting...' : 'Export Users'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExportUsersModal;